import React, { useState, useEffect, useCallback } from 'react';

const CATEGORIES = ['Tout', 'Mode', 'Beauté', 'Lifestyle', 'Sport', 'Gaming', 'Food', 'Tech', 'Voyage', 'Humour', 'Musique', 'Autre'];

const DEFAULT_PRIX_POST = 2900;
const DEFAULT_PRIX_ABO = 349; // €/mois HT

const emptyForm = {
  nom: '', pseudo: '', categorie: 'Lifestyle', photo_url: '',
  instagram: '', tiktok: '', youtube: '',
  abonnes: 0, bio: '',
  prix_post: DEFAULT_PRIX_POST, prix_abo_trimestre: DEFAULT_PRIX_ABO,
  actif: 1, ordre: 0
};

function InfluenceursManagement({ token }) {
  const [influenceurs, setInfluenceurs] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [loading, setLoading] = useState(false);

  const [showPdfModal, setShowPdfModal] = useState(false);
  const [pdfFile, setPdfFile] = useState(null);
  const [pdfUploading, setPdfUploading] = useState(false);
  const [pdfResult, setPdfResult] = useState(null);
  const [candidatsToImport, setCandidatsToImport] = useState([]);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch('/api/influenceurs', {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      setInfluenceurs(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error('Erreur chargement influenceurs:', e);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const openNew = () => {
    setEditing(null);
    setForm(emptyForm);
    setShowModal(true);
  };

  const openEdit = (inf) => {
    setEditing(inf);
    setForm({
      nom: inf.nom || '',
      pseudo: inf.pseudo || '',
      categorie: inf.categorie || 'Lifestyle',
      photo_url: inf.photo_url || '',
      instagram: inf.instagram || '',
      tiktok: inf.tiktok || '',
      youtube: inf.youtube || '',
      abonnes: inf.abonnes || 0,
      bio: inf.bio || '',
      prix_post: inf.prix_post || 0,
      prix_abo_trimestre: inf.prix_abo_trimestre || 0,
      actif: inf.actif ? 1 : 0,
      ordre: inf.ordre || 0
    });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.nom) { alert('Le nom est obligatoire'); return; }
    const url = editing ? `/api/influenceurs/${editing.id}` : '/api/influenceurs';
    const method = editing ? 'PUT' : 'POST';
    try {
      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(form)
      });
      if (res.ok) {
        setShowModal(false);
        fetchAll();
      } else {
        const err = await res.json();
        alert(err.error || 'Erreur');
      }
    } catch (e) {
      alert('Erreur réseau');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer cet influenceur ?')) return;
    try {
      const res = await fetch(`/api/influenceurs/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) fetchAll();
    } catch (e) {
      alert('Erreur suppression');
    }
  };

  const handlePdfUpload = async () => {
    if (!pdfFile) { alert('Sélectionnez un fichier PDF'); return; }
    setPdfUploading(true);
    setPdfResult(null);
    setCandidatsToImport([]);
    const fd = new FormData();
    fd.append('pdf', pdfFile);
    try {
      const res = await fetch('/api/influenceurs/import-pdf', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: fd
      });
      const data = await res.json();
      if (res.ok) {
        setPdfResult(data);
        setCandidatsToImport(data.candidats || []);
      } else {
        alert(data.error || 'Erreur upload');
      }
    } catch (e) {
      alert('Erreur réseau (PDF trop volumineux ?)');
    } finally {
      setPdfUploading(false);
    }
  };

  const handleBulkInsert = async () => {
    if (candidatsToImport.length === 0) return;
    if (!window.confirm(`Importer ${candidatsToImport.length} influenceurs ?`)) return;
    try {
      const res = await fetch('/api/influenceurs/bulk-insert', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ influenceurs: candidatsToImport })
      });
      const data = await res.json();
      if (res.ok) {
        alert(`${data.inserted} influenceurs importés`);
        setShowPdfModal(false);
        setPdfFile(null);
        setPdfResult(null);
        setCandidatsToImport([]);
        fetchAll();
      } else {
        alert(data.error || 'Erreur import');
      }
    } catch (e) {
      alert('Erreur réseau');
    }
  };

  const updateCandidat = (idx, field, value) => {
    const next = [...candidatsToImport];
    next[idx] = { ...next[idx], [field]: value };
    setCandidatsToImport(next);
  };

  const removeCandidat = (idx) => {
    setCandidatsToImport(candidatsToImport.filter((_, i) => i !== idx));
  };

  return (
    <div style={{ padding: '20px', maxWidth: '1400px', margin: '0 auto' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <h2 style={{ margin: 0 }}>Influenceurs ({influenceurs.length})</h2>
        <div style={{ display: 'flex', gap: '10px' }}>
          <button
            onClick={() => setShowPdfModal(true)}
            style={{ padding: '10px 16px', background: '#0ea5e9', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer' }}
          >
            Importer PDF
          </button>
          <button
            onClick={openNew}
            style={{ padding: '10px 16px', background: '#10b981', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer' }}
          >
            + Nouvel influenceur
          </button>
        </div>
      </div>

      <div style={{ marginBottom: '12px', padding: '10px', background: '#f1f5f9', borderRadius: '6px', fontSize: '13px', color: '#475569' }}>
        Boutique publique : <a href="/boutique-influenceurs" target="_blank" rel="noopener noreferrer">/boutique-influenceurs</a>
      </div>

      {loading ? (
        <p>Chargement...</p>
      ) : influenceurs.length === 0 ? (
        <p style={{ color: '#64748b', textAlign: 'center', padding: '40px' }}>
          Aucun influenceur. Cliquez sur "Importer PDF" ou "+ Nouvel influenceur" pour commencer.
        </p>
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse', background: '#fff', borderRadius: '8px', overflow: 'hidden' }}>
          <thead style={{ background: '#0f172a', color: '#fff' }}>
            <tr>
              <th style={{ padding: '12px', textAlign: 'left' }}>Photo</th>
              <th style={{ padding: '12px', textAlign: 'left' }}>Nom</th>
              <th style={{ padding: '12px', textAlign: 'left' }}>Catégorie</th>
              <th style={{ padding: '12px', textAlign: 'right' }}>Prix/post</th>
              <th style={{ padding: '12px', textAlign: 'right' }}>Abo €/mois</th>
              <th style={{ padding: '12px', textAlign: 'center' }}>Actif</th>
              <th style={{ padding: '12px', textAlign: 'center' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {influenceurs.map(inf => (
              <tr key={inf.id} style={{ borderBottom: '1px solid #e2e8f0' }}>
                <td style={{ padding: '8px' }}>
                  {inf.photo_url ? (
                    <img src={inf.photo_url} alt={inf.nom} style={{ width: 48, height: 48, borderRadius: '50%', objectFit: 'cover' }} />
                  ) : (
                    <div style={{ width: 48, height: 48, borderRadius: '50%', background: '#cbd5e1', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: 'bold' }}>
                      {inf.nom?.charAt(0)}
                    </div>
                  )}
                </td>
                <td style={{ padding: '12px' }}>
                  <div style={{ fontWeight: 600 }}>{inf.nom}</div>
                  {inf.pseudo && <div style={{ fontSize: 12, color: '#64748b' }}>@{inf.pseudo}</div>}
                </td>
                <td style={{ padding: '12px' }}>{inf.categorie || '-'}</td>
                <td style={{ padding: '12px', textAlign: 'right' }}>{parseFloat(inf.prix_post || 0).toFixed(0)} €</td>
                <td style={{ padding: '12px', textAlign: 'right' }}>{parseFloat(inf.prix_abo_trimestre || 0).toFixed(0)} €/mois</td>
                <td style={{ padding: '12px', textAlign: 'center' }}>
                  <span style={{
                    padding: '4px 10px', borderRadius: 12, fontSize: 12,
                    background: inf.actif ? '#dcfce7' : '#fee2e2',
                    color: inf.actif ? '#166534' : '#991b1b'
                  }}>
                    {inf.actif ? 'Actif' : 'Inactif'}
                  </span>
                </td>
                <td style={{ padding: '12px', textAlign: 'center' }}>
                  <button onClick={() => openEdit(inf)} style={{ marginRight: 6, padding: '6px 12px', background: '#f1f5f9', border: '1px solid #cbd5e1', borderRadius: 4, cursor: 'pointer' }}>Modifier</button>
                  <button onClick={() => handleDelete(inf.id)} style={{ padding: '6px 12px', background: '#fee2e2', color: '#991b1b', border: '1px solid #fecaca', borderRadius: 4, cursor: 'pointer' }}>Suppr.</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {/* Modal CRUD */}
      {showModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: '#fff', borderRadius: 8, width: '90%', maxWidth: 700, maxHeight: '90vh', overflow: 'auto' }}>
            <div style={{ padding: 20, borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between' }}>
              <h3 style={{ margin: 0 }}>{editing ? 'Modifier' : 'Nouvel'} influenceur</h3>
              <button onClick={() => setShowModal(false)} style={{ border: 'none', background: 'none', fontSize: 24, cursor: 'pointer' }}>×</button>
            </div>
            <form onSubmit={handleSubmit} style={{ padding: 20 }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div>
                  <label>Nom *</label>
                  <input type="text" value={form.nom} onChange={e => setForm({ ...form, nom: e.target.value })} required style={{ width: '100%', padding: 8, marginTop: 4 }} />
                </div>
                <div>
                  <label>Pseudo</label>
                  <input type="text" value={form.pseudo} onChange={e => setForm({ ...form, pseudo: e.target.value })} style={{ width: '100%', padding: 8, marginTop: 4 }} />
                </div>
                <div>
                  <label>Catégorie</label>
                  <select value={form.categorie} onChange={e => setForm({ ...form, categorie: e.target.value })} style={{ width: '100%', padding: 8, marginTop: 4 }}>
                    {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div>
                  <label>Abonnés</label>
                  <input type="number" value={form.abonnes} onChange={e => setForm({ ...form, abonnes: parseInt(e.target.value) || 0 })} style={{ width: '100%', padding: 8, marginTop: 4 }} />
                </div>
                <div style={{ gridColumn: 'span 2' }}>
                  <label>Photo URL</label>
                  <input type="url" value={form.photo_url} onChange={e => setForm({ ...form, photo_url: e.target.value })} placeholder="https://..." style={{ width: '100%', padding: 8, marginTop: 4 }} />
                </div>
                <div>
                  <label>Instagram</label>
                  <input type="text" value={form.instagram} onChange={e => setForm({ ...form, instagram: e.target.value })} placeholder="@compte" style={{ width: '100%', padding: 8, marginTop: 4 }} />
                </div>
                <div>
                  <label>TikTok</label>
                  <input type="text" value={form.tiktok} onChange={e => setForm({ ...form, tiktok: e.target.value })} placeholder="@compte" style={{ width: '100%', padding: 8, marginTop: 4 }} />
                </div>
                <div style={{ gridColumn: 'span 2' }}>
                  <label>YouTube</label>
                  <input type="text" value={form.youtube} onChange={e => setForm({ ...form, youtube: e.target.value })} placeholder="@compte ou URL" style={{ width: '100%', padding: 8, marginTop: 4 }} />
                </div>
                <div style={{ gridColumn: 'span 2' }}>
                  <label>Bio / Description</label>
                  <textarea value={form.bio} onChange={e => setForm({ ...form, bio: e.target.value })} rows={4} style={{ width: '100%', padding: 8, marginTop: 4 }} />
                </div>
                <div>
                  <label>Prix par post (€)</label>
                  <input type="number" step="0.01" value={form.prix_post} onChange={e => setForm({ ...form, prix_post: parseFloat(e.target.value) || 0 })} style={{ width: '100%', padding: 8, marginTop: 4 }} />
                </div>
                <div>
                  <label>Abonnement trimestriel (€)</label>
                  <input type="number" step="0.01" value={form.prix_abo_trimestre} onChange={e => setForm({ ...form, prix_abo_trimestre: parseFloat(e.target.value) || 0 })} style={{ width: '100%', padding: 8, marginTop: 4 }} />
                </div>
                <div>
                  <label>Statut</label>
                  <select value={form.actif} onChange={e => setForm({ ...form, actif: parseInt(e.target.value) })} style={{ width: '100%', padding: 8, marginTop: 4 }}>
                    <option value={1}>Actif (visible boutique)</option>
                    <option value={0}>Inactif</option>
                  </select>
                </div>
                <div>
                  <label>Ordre d'affichage</label>
                  <input type="number" value={form.ordre} onChange={e => setForm({ ...form, ordre: parseInt(e.target.value) || 0 })} style={{ width: '100%', padding: 8, marginTop: 4 }} />
                </div>
              </div>
              <div style={{ marginTop: 20, display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                <button type="button" onClick={() => setShowModal(false)} style={{ padding: '10px 16px', background: '#e2e8f0', border: 'none', borderRadius: 6, cursor: 'pointer' }}>Annuler</button>
                <button type="submit" style={{ padding: '10px 16px', background: '#10b981', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer' }}>
                  {editing ? 'Enregistrer' : 'Créer'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal Import PDF */}
      {showPdfModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: '#fff', borderRadius: 8, width: '95%', maxWidth: 1100, maxHeight: '90vh', overflow: 'auto' }}>
            <div style={{ padding: 20, borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between' }}>
              <h3 style={{ margin: 0 }}>Importer un PDF d'influenceurs</h3>
              <button onClick={() => { setShowPdfModal(false); setPdfFile(null); setPdfResult(null); setCandidatsToImport([]); }} style={{ border: 'none', background: 'none', fontSize: 24, cursor: 'pointer' }}>×</button>
            </div>
            <div style={{ padding: 20 }}>
              {!pdfResult ? (
                <>
                  <p style={{ color: '#475569' }}>
                    Le serveur extraira le texte du PDF et tentera de détecter les influenceurs.
                    Vous pourrez vérifier et corriger avant l'import définitif.
                    <br/>Limite : <b>300 Mo max</b>.
                  </p>
                  <input type="file" accept="application/pdf" onChange={e => setPdfFile(e.target.files[0])} style={{ marginTop: 12, marginBottom: 12 }} />
                  {pdfFile && (
                    <div style={{ padding: 8, background: '#f1f5f9', borderRadius: 4, marginBottom: 12 }}>
                      <strong>{pdfFile.name}</strong> — {(pdfFile.size / (1024 * 1024)).toFixed(2)} Mo
                    </div>
                  )}
                  <button
                    onClick={handlePdfUpload}
                    disabled={!pdfFile || pdfUploading}
                    style={{ padding: '10px 16px', background: '#0ea5e9', color: '#fff', border: 'none', borderRadius: 6, cursor: pdfUploading ? 'wait' : 'pointer', opacity: pdfUploading ? 0.6 : 1 }}
                  >
                    {pdfUploading ? 'Analyse en cours...' : 'Lancer l\'analyse'}
                  </button>
                </>
              ) : (
                <>
                  <div style={{ padding: 12, background: '#f0f9ff', borderRadius: 6, marginBottom: 16 }}>
                    <div>Taille fichier : <b>{pdfResult.file_size_mb} Mo</b></div>
                    <div>Caractères extraits : <b>{pdfResult.texte_extrait_chars.toLocaleString('fr-FR')}</b></div>
                    <div>Candidats détectés : <b>{pdfResult.candidats_detectes}</b></div>
                  </div>

                  {candidatsToImport.length === 0 ? (
                    <p style={{ color: '#dc2626' }}>
                      Aucun candidat détecté automatiquement. Le PDF est probablement composé d'images
                      (le parsing texte ne fonctionne pas sur ce type de PDF).
                      <br/>Ajoutez les influenceurs manuellement avec "+ Nouvel influenceur".
                    </p>
                  ) : (
                    <>
                      <p>Vérifiez et corrigez les données ci-dessous. Décochez ou supprimez les lignes incorrectes.</p>
                      <div style={{ maxHeight: 400, overflow: 'auto', border: '1px solid #e2e8f0', borderRadius: 6 }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                          <thead style={{ background: '#f1f5f9', position: 'sticky', top: 0 }}>
                            <tr>
                              <th style={{ padding: 8, textAlign: 'left' }}>Nom</th>
                              <th style={{ padding: 8, textAlign: 'left' }}>Catégorie</th>
                              <th style={{ padding: 8, textAlign: 'right' }}>Prix/post (€)</th>
                              <th style={{ padding: 8, textAlign: 'right' }}>Abo (€/mois)</th>
                              <th style={{ padding: 8 }}></th>
                            </tr>
                          </thead>
                          <tbody>
                            {candidatsToImport.map((c, idx) => (
                              <tr key={idx} style={{ borderBottom: '1px solid #e2e8f0' }}>
                                <td style={{ padding: 4 }}>
                                  <input type="text" value={c.nom} onChange={e => updateCandidat(idx, 'nom', e.target.value)} style={{ width: '100%', padding: 4 }} />
                                </td>
                                <td style={{ padding: 4 }}>
                                  <select value={c.categorie || 'Lifestyle'} onChange={e => updateCandidat(idx, 'categorie', e.target.value)} style={{ width: '100%', padding: 4 }}>
                                    {CATEGORIES.map(cat => <option key={cat} value={cat}>{cat}</option>)}
                                    {c.categorie && !CATEGORIES.includes(c.categorie) && <option value={c.categorie}>{c.categorie}</option>}
                                  </select>
                                </td>
                                <td style={{ padding: 4 }}>
                                  <input
                                    type="number"
                                    step="1"
                                    value={c.prix_post || DEFAULT_PRIX_POST}
                                    onChange={e => updateCandidat(idx, 'prix_post', parseFloat(e.target.value) || 0)}
                                    style={{ width: 90, padding: 4, textAlign: 'right' }}
                                  />
                                </td>
                                <td style={{ padding: 4 }}>
                                  <input
                                    type="number"
                                    step="1"
                                    value={c.prix_abo_trimestre || DEFAULT_PRIX_ABO}
                                    onChange={e => updateCandidat(idx, 'prix_abo_trimestre', parseFloat(e.target.value) || 0)}
                                    style={{ width: 90, padding: 4, textAlign: 'right' }}
                                  />
                                </td>
                                <td style={{ padding: 4 }}>
                                  <button onClick={() => removeCandidat(idx)} style={{ padding: '2px 8px', background: '#fee2e2', color: '#991b1b', border: 'none', borderRadius: 4, cursor: 'pointer' }}>×</button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                      <div style={{ marginTop: 16, display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
                        <button onClick={() => { setPdfResult(null); setCandidatsToImport([]); setPdfFile(null); }} style={{ padding: '10px 16px', background: '#e2e8f0', border: 'none', borderRadius: 6, cursor: 'pointer' }}>
                          Recommencer
                        </button>
                        <button onClick={handleBulkInsert} style={{ padding: '10px 16px', background: '#10b981', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer' }}>
                          Importer {candidatsToImport.length} influenceurs
                        </button>
                      </div>
                    </>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default InfluenceursManagement;
